import * as React from "react";
import { ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const solutionCategories = [
  {
    label: "Healthcare & Clinics",
    description: "Medical and health practices that run on appointments.",
    items: [
      { name: "Dental Clinics", href: "/solutions/dental-clinics" },
      { name: "Med Spas", href: "/solutions/med-spas" },
      { name: "Chiropractors", href: "/solutions/chiropractors" },
      { name: "Veterinary Clinics", href: "/solutions/veterinary-clinics" },
      { name: "Orthodontists", href: "/solutions/orthodontists" },
      { name: "Dermatologists", href: "/solutions/dermatologists" },
      { name: "Psychologists & Therapists", href: "/solutions/therapists" },
      { name: "Optometrists", href: "/solutions/optometrists" },
      { name: "Home Healthcare Services", href: "/solutions/home-healthcare" },
      { name: "Cosmetic Surgeons", href: "/solutions/cosmetic-surgeons" },
      { name: "Dieticians & Nutritionists", href: "/solutions/nutritionists" },
    ],
  },
  {
    label: "Beauty & Wellness",
    description: "High-volume service businesses that can't miss calls.",
    items: [
      { name: "Hair Salons", href: "/solutions/hair-salons" },
      { name: "Nail Salons", href: "/solutions/nail-salons" },
      { name: "Beauty Clinics", href: "/solutions/beauty-clinics" },
      { name: "Massage Therapists", href: "/solutions/massage-therapy" },
      { name: "Physical Therapy", href: "/solutions/physical-therapy" },
      { name: "Massage Therapy Centers", href: "/solutions/massage-centers" },
      { name: "Spa & Wellness Centers", href: "/solutions/spas" },
    ],
  },
  {
    label: "Auto & Repair",
    description: "Phone-heavy shops where every lead matters.",
    items: [
      { name: "Auto Repair Shops", href: "/solutions/auto-repair" },
      { name: "Body Shops", href: "/solutions/body-shops" },
      { name: "Car Wash & Detailing", href: "/solutions/car-wash-detailing" },
      { name: "Towing Services", href: "/solutions/towing" },
      { name: "Mobile Mechanics", href: "/solutions/mobile-mechanics" },
      { name: "Auto Glass Repair", href: "/solutions/auto-glass" },
    ],
  },
  {
    label: "Home Services",
    description: "Trades where missed calls = missed jobs.",
    items: [
      { name: "HVAC", href: "/solutions/hvac" },
      { name: "Plumbing", href: "/solutions/plumbing" },
      { name: "Electricians", href: "/solutions/electricians" },
      { name: "Pest Control", href: "/solutions/pest-control" },
      { name: "Landscaping", href: "/solutions/landscaping" },
      { name: "Cleaning Services", href: "/solutions/cleaning" },
      { name: "Roofing", href: "/solutions/roofing" },
      { name: "Garage Door Repair", href: "/solutions/garage-door" },
      { name: "Solar Installers", href: "/solutions/solar" },
      { name: "Handymen", href: "/solutions/handyman" },
      { name: "Carpet Cleaning", href: "/solutions/carpet-cleaning" },
    ],
  },
  {
    label: "Pet Services",
    description: "Pet-focused businesses with constant phone traffic.",
    items: [
      { name: "Dog Grooming", href: "/solutions/dog-grooming" },
      { name: "Dog Boarding & Kennels", href: "/solutions/dog-boarding" },
      { name: "Pet Training", href: "/solutions/pet-training" },
      { name: "Pet Daycare", href: "/solutions/pet-daycare" },
      { name: "Pet Clinics", href: "/solutions/pet-clinics" },
    ],
  },
  {
    label: "Retail & Local Services",
    description: "Local shops that rely on quick answers and updates.",
    items: [
      { name: "Laundromats", href: "/solutions/laundromats" },
      { name: "Dry Cleaners", href: "/solutions/dry-cleaners" },
      { name: "Tailoring & Alterations", href: "/solutions/alterations" },
      { name: "Delivery Services", href: "/solutions/delivery" },
      { name: "Repair Centers", href: "/solutions/repair-centers" },
    ],
  },
  {
    label: "Hospitality & Events",
    description: "Where booking calls come all day long.",
    items: [
      { name: "Bed & Breakfasts", href: "/solutions/bed-and-breakfasts" },
      {
        name: "Small Hotels & Airbnb Managers",
        href: "/solutions/small-hotels-airbnb",
      },
      { name: "Event Venues", href: "/solutions/event-venues" },
      { name: "Wedding Planners", href: "/solutions/wedding-planners" },
      { name: "Party Rentals", href: "/solutions/party-rentals" },
    ],
  },
  {
    label: "Professional & Financial Services",
    description: "High-value calls that must be answered fast.",
    items: [
      { name: "Real Estate Agencies", href: "/solutions/real-estate" },
      { name: "Property Management", href: "/solutions/property-management" },
      { name: "Insurance Agencies", href: "/solutions/insurance" },
      { name: "Law Firms", href: "/solutions/law-firms" },
      { name: "Accountant Offices", href: "/solutions/accountants" },
      { name: "Immigration Consultants", href: "/solutions/immigration" },
      { name: "Loan Brokers", href: "/solutions/loan-brokers" },
      { name: "Moving Companies", href: "/solutions/moving-companies" },
    ],
  },
  {
    label: "Emergency & 24/7",
    description: "Urgent-response businesses where speed is everything.",
    items: [
      { name: "Emergency Dental", href: "/solutions/emergency-dental" },
      { name: "Emergency Vet", href: "/solutions/emergency-vet" },
      { name: "24/7 Plumbing", href: "/solutions/24-7-plumbing" },
      { name: "Roadside Assistance", href: "/solutions/roadside-assistance" },
    ],
  },
  {
    label: "Community & Non-Profit",
    description: "Inbound calls for support, info, and coordination.",
    items: [
      { name: "Churches", href: "/solutions/churches" },
      { name: "NGO Helplines", href: "/solutions/ngo-helplines" },
      { name: "Community Centers", href: "/solutions/community-centers" },
    ],
  },
];

export function SolutionsMegaMenu({ className }) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          "inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900 focus:outline-none",
          className
        )}
      >
        <span>Who we serve</span>
        <ChevronDown className="h-4 w-4" />
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-[960px] max-h-[520px] overflow-y-auto border-slate-200 bg-white text-slate-900 shadow-2xl backdrop-blur-xl"
      >
        <div className="px-4 pt-3 pb-4 border-b border-slate-200">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">
            Industry Solutions
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Calyro.ai powers phone calls, bookings, and leads across dozens of
            service businesses.
          </p>
        </div>

        <div className="grid gap-4 p-4 md:grid-cols-2 lg:grid-cols-3">
          {solutionCategories.map((category) => (
            <div
              key={category.label}
              className="rounded-xl border border-slate-200 bg-slate-50 p-3 hover:border-blue-400 hover:bg-blue-50 transition-colors"
            >
              <p className="text-sm font-semibold text-slate-900">
                {category.label}
              </p>
              {category.description && (
                <p className="mt-1 text-xs text-slate-600">
                  {category.description}
                </p>
              )}

              <ul className="mt-2 space-y-1.5">
                {category.items.map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href ?? "#"}
                      className="group inline-flex items-center text-xs text-slate-700 hover:text-blue-600"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400 mr-2 group-hover:bg-blue-500" />
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2.5 text-[11px] text-slate-600">
          <span>
            Don&apos;t see your industry?{" "}
            <a
              href="/contact"
              className="font-medium text-blue-600 hover:text-blue-700"
            >
              Talk to us
            </a>{" "}
            — Calyro.ai can be trained for any call-driven business.
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
